import Controllers from './Controllers.js';
import Helpers from './Helpers.js';

/**
 * Support — the Intercom side of the bar. Intercom is booted elsewhere
 * (GTM / template-parts/global/intercom-setup-script.php, gated by
 * thryvData.intercom_status); this module only repositions the messenger
 * for the bar and keeps the Support button state in sync.
 *
 * Hooking happens at bar init (not on the first Support click): Intercom
 * persists an open messenger across page views, and a messenger restored
 * on page load would otherwise render at the boot layer's
 * vertical_padding (set for the old Thryver launcher layout — GTM boots
 * with 125) and float mid-air above the bar.
 */

var Support = new Object();

/**
 * Run-once guard for init — the intercom-ready listener and availability
 * poll are only armed once per page.
 */
Support.armed = false;

/**
 * Run-once guard for hook — Intercom's update/onShow/onHide are only
 * registered once, whichever path (event, poll, click safety) gets there
 * first.
 */
Support.hooked = false;

/**
 * Arm the Intercom hooks (called once from Component.init on bar pages).
 * Intercom loads deferred (GTM / window load), so wait for it: the
 * theme's loader dispatches `intercom-ready`, and a poll covers the GTM
 * boot path. Calling Intercom() against the pre-boot queue stub is fine —
 * the calls apply at boot.
 *
 * @param {object} component  Alpine component
 */
Support.init = function (component) {

  var _this = this;

  if (_this.armed) return;

  _this.armed = true;

  if (!Controllers.intercomAvailable()) {

    // Intercom disabled on this page (no Support button either) — the
    // hooks would never matter.
    if (typeof window.Intercom !== 'function') return;

  }

  if (typeof window.Intercom === 'function') {

    _this.hook(component);

    return;

  }

  document.addEventListener('intercom-ready', function () {

    _this.hook(component);

  });

  // GTM's boot path doesn't dispatch intercom-ready — poll as a fallback.
  var attempts = 0;
  var waitForIntercom = setInterval(function () {

    if (typeof window.Intercom === 'function') {

      clearInterval(waitForIntercom);

      _this.hook(component);

      return;

    }

    if (++attempts >= 40) clearInterval(waitForIntercom); // ~20s

  }, 500);

};

/**
 * Reposition the messenger above the bar and register the visibility
 * callbacks that keep supportOpen in sync — they also fire when Intercom
 * restores a persisted-open messenger on page load, so it appears at the
 * bar position with the Support button correctly active.
 *
 * @param {object} component  Alpine component
 */
Support.hook = function (component) {

  var _this = this;

  if (_this.hooked) return;

  _this.hooked = true;

  // Lift the messenger above the bar (same clearance as the Book/Call
  // panels, bottom: 94px), overriding the boot layer's Thryver-launcher
  // value.
  window.Intercom('update', { vertical_padding: 94 });

  window.Intercom('onShow', function () {

    component.supportOpen = true;

  });

  window.Intercom('onHide', function () {

    component.supportOpen = false;

  });

  Helpers.log(component, 'Support.hook — Intercom repositioned for the bar');

};

export default Support;
